// Net flows (C-6c): money into or out of a product, as units issued minus
// units redeemed, valued at each day's price. A price move is not a flow,
// and a product or chain newly tracked is not a flow either. Pure helpers
// for the tables, tiles and bar charts; null always means "not measured".

import { compactUsd } from "./format";

export type FlowWindow = "1d" | "7d" | "30d";

export const FLOW_WINDOWS: ReadonlyArray<readonly [FlowWindow, string]> = [
  ["1d", "1d"],
  ["7d", "7d"],
  ["30d", "30d"],
];

export type FlowMethod = "transfer_index" | "supply_delta" | "mixed";

/** The flow fields the API puts on entries, tokens, totals and class rows. */
export interface FlowFields {
  net_flow_1d_usd?: number | null;
  net_flow_7d_usd?: number | null;
  net_flow_30d_usd?: number | null;
  flow_method?: FlowMethod | null;
}

/** Below a cent is rounding noise (the API can send −1e-9), read as zero. */
export const FLOW_EPSILON = 0.01;

/** A flow as a number, |x| under a cent as 0, null when not measured. */
export function cleanFlow(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return Math.abs(value) < FLOW_EPSILON ? 0 : value;
}

export function flowFor(fields: FlowFields, window: FlowWindow): number | null {
  return cleanFlow(window === "1d" ? fields.net_flow_1d_usd : window === "7d" ? fields.net_flow_7d_usd : fields.net_flow_30d_usd);
}

/** "+$6.9M" in, "−$27.3K" out, "$0" flat; "—" when not measured. */
export function flowText(value: number | null): string {
  if (value === null) return "—";
  if (value === 0) return "$0";
  return `${value > 0 ? "+" : "−"}${compactUsd(Math.abs(value))}`;
}

export function flowTone(value: number | null): "up" | "down" | null {
  return value === null || value === 0 ? null : value > 0 ? "up" : "down";
}

/** The hover text: how the flow was measured. */
export function flowMethodText(method: FlowMethod | null | undefined): string | undefined {
  switch (method) {
    case "transfer_index":
      return "From issuance and redemption on chain (mints minus burns, from our own transfer index)";
    case "supply_delta":
      return "From the change in supply × price";
    case "mixed":
      return "Partly from issuance and redemption on chain, partly from the change in supply × price";
    default:
      return undefined;
  }
}

/** [epoch ms, flow] per day for the bar strip; unmeasured days stay null, not zero. */
export function dailyFlows(points: ReadonlyArray<{ date: string; net_flow_usd?: number | null }>): Array<[number, number | null]> {
  return points
    .map((p): [number, number | null] => [Date.parse(`${p.date}T00:00:00Z`), cleanFlow(p.net_flow_usd)])
    .filter(([t]) => Number.isFinite(t))
    .sort((a, b) => a[0] - b[0]);
}
